// src/components/shops/postTemplates/FieldNote.tsx
//
// The spec sheet: origin, altitude, process, tasting notes, in labelled rows.
//
// Specialty shops care most about exactly this information and almost never
// make it look good — it usually arrives as a paragraph nobody finishes. Set as
// a table it becomes scannable, and it borrows the zine palette the Field
// Report already uses so a shop's coffee posts and its reports feel related.

import { cachedUrl } from '../../../lib/storageUrl'
import SocialBrewBadge from '../SocialBrewBadge'
import { parseRows, type TemplateProps } from './types'

export default function FieldNote({
  title, body, photos, shopName, rating, ratingCount, scale = 1,
}: TemplateProps) {
  const px = (n: number) => `${n * scale}px`
  const rows = parseRows(body)

  return (
    <div
      className="overflow-hidden"
      style={{ background: '#F1E9D6', borderRadius: px(2) }}
      data-shop={shopName}
    >
      {photos.length > 0 && (
        <div style={{ aspectRatio: '3 / 2' }}>
          <img src={cachedUrl(photos[0])} alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
      )}

      <div style={{ padding: `${px(18)} ${px(16)} ${px(14)}` }}>
        <p
          style={{
            fontSize: px(8.5),
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: '#437B78',
            fontWeight: 700,
            marginBottom: px(7),
          }}
        >
          Field note
        </p>
        <h3
          className="font-display"
          style={{
            fontSize: px(21),
            lineHeight: 1.14,
            color: '#211813',
            fontWeight: 700,
            margin: 0,
            textWrap: 'balance',
          }}
        >
          {title}
        </h3>

        <div style={{ borderTop: `${px(2)} solid #CC5B21`, marginTop: px(12), paddingTop: px(10) }}>
          {rows ? (
            rows.map((r, i) => (
              <div
                key={i}
                className="flex items-baseline"
                style={{
                  gap: px(10),
                  paddingBottom: px(7),
                  marginBottom: px(7),
                  borderBottom: i === rows.length - 1 ? 'none' : `${px(1)} solid rgba(33,24,19,0.12)`,
                }}
              >
                <span
                  style={{
                    fontSize: px(9),
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: '#437B78',
                    fontWeight: 700,
                    width: px(86),
                    flexShrink: 0,
                  }}
                >
                  {r.label}
                </span>
                <span style={{ fontSize: px(13), lineHeight: 1.45, color: '#211813', fontWeight: 600 }}>
                  {r.value}
                </span>
              </div>
            ))
          ) : (
            body && (
              <p style={{ fontSize: px(13), lineHeight: 1.65, color: '#211813', margin: 0 }}>{body}</p>
            )
          )}
        </div>

        <div style={{ marginTop: px(16) }}>
          <SocialBrewBadge rating={rating} ratingCount={ratingCount} tone="light" scale={scale} />
        </div>
      </div>
    </div>
  )
}
